import { dataService } from './dataService';
import { ApiError, apiRequest, clearStoredAuthToken, getStoredAuthToken, isApiConfigured, storeAuthToken } from '../utils/apiClient';

export interface UserInfo {
  id: string;
  uid: string;
  email?: string;
  phone?: string;
  nickname?: string;
  isAdmin?: boolean;
  avatarUrl?: string;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  createdAt?: string;
  lastSignInAt?: string;
}

type AuthListener = (user: UserInfo | null) => void;

const listeners = new Set<AuthListener>();
let currentUser: UserInfo | null = null;
let currentUserPromise: Promise<UserInfo | null> | null = null;

function normalizeUser(user: any): UserInfo {
  return {
    id: user.id,
    uid: user.uid || user.id,
    email: user.email,
    nickname: user.nickname,
    isAdmin: Boolean(user.isAdmin),
    emailVerified: Boolean(user.emailVerified),
    createdAt: user.createdAt,
    lastSignInAt: new Date().toISOString(),
  };
}

function emitAuthState(user: UserInfo | null): void {
  currentUser = user;
  for (const listener of listeners) {
    listener(user);
  }
}

async function createDefaultProfileIfNeeded(user: UserInfo): Promise<void> {
  try {
    dataService.setUserId(user.id);
    await dataService.createUserProfile(user.id, user.email || '', user.nickname || '用户');
  } catch (error) {
    console.warn('[Auth] 创建默认用户档案失败:', error);
  }
}

export async function getCurrentUser(): Promise<UserInfo | null> {
  if (!isApiConfigured()) {
    return null;
  }

  if (currentUser) {
    return currentUser;
  }

  const token = getStoredAuthToken();
  if (!token) {
    return null;
  }

  if (currentUserPromise) {
    return currentUserPromise;
  }

  currentUserPromise = (async () => {
    try {
      const response = await apiRequest<{ user: UserInfo }>('/api/auth/session');
      const user = normalizeUser(response.user);
      currentUser = user;
      return user;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        clearStoredAuthToken();
        currentUser = null;
      }
      console.warn('[Auth] 恢复会话失败:', error);
      return null;
    } finally {
      currentUserPromise = null;
    }
  })();

  return currentUserPromise;
}

export function onAuthStateChanged(callback: (user: UserInfo | null) => void): () => void {
  listeners.add(callback);
  callback(currentUser);
  return () => {
    listeners.delete(callback);
  };
}

export async function logout(): Promise<void> {
  try {
    if (getStoredAuthToken()) {
      await apiRequest('/api/auth/logout', { method: 'POST' });
    }
  } finally {
    clearStoredAuthToken();
    emitAuthState(null);
  }
}

export async function loginWithEmail(email: string, password: string): Promise<UserInfo> {
  const response = await apiRequest<{ user: UserInfo; token: string }>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  storeAuthToken(response.token);
  const user = normalizeUser(response.user);
  emitAuthState(user);
  return user;
}

export async function sendPasswordResetEmail(_email?: string): Promise<void> {
  throw new Error('当前自托管版本暂不支持邮箱重置密码，请使用已登录状态修改密码');
}

export class AuthService {
  validateEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  checkPasswordStrength(password: string): { valid: boolean; message: string } {
    if (password.length < 6) {
      return { valid: false, message: '密码长度至少6位' };
    }
    return { valid: true, message: '' };
  }

  async registerWithPassword(email: string, password: string, nickname?: string): Promise<UserInfo> {
    const response = await apiRequest<{ user: UserInfo; token: string }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, nickname }),
    });
    storeAuthToken(response.token);
    const user = normalizeUser(response.user);
    emitAuthState(user);
    await createDefaultProfileIfNeeded(user);
    return user;
  }

  async loginWithPassword(email: string, password: string): Promise<UserInfo> {
    return loginWithEmail(email, password);
  }

  async logout(): Promise<void> {
    await logout();
  }

  async sendRegisterCode(): Promise<void> {
    throw new Error('当前自托管版本已改为邮箱密码直接注册');
  }

  async completeRegistration(): Promise<UserInfo> {
    throw new Error('当前自托管版本已改为邮箱密码直接注册');
  }

  async sendLoginCode(): Promise<void> {
    throw new Error('当前自托管版本暂不支持验证码登录');
  }

  async loginWithCode(): Promise<UserInfo> {
    throw new Error('当前自托管版本暂不支持验证码登录');
  }

  async sendResetPasswordEmail(email: string): Promise<void> {
    await sendPasswordResetEmail(email);
  }

  async setPassword(): Promise<void> {
    throw new Error('当前自托管版本请直接在注册时设置密码');
  }

  async sendPasswordResetCode(_email?: string): Promise<void> {
    throw new Error('当前自托管版本暂不支持邮箱验证码重置密码');
  }

  async verifyCodeAndSetPassword(_code?: string, _newPassword?: string): Promise<void> {
    throw new Error('当前自托管版本暂不支持邮箱验证码重置密码');
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await apiRequest('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ oldPassword, newPassword }),
    });
  }

  async hasPassword(): Promise<boolean> {
    return Boolean(currentUser || await getCurrentUser());
  }
}

export const authService = new AuthService();

export function isSelfHostedAuthConfigured(): boolean {
  return isApiConfigured();
}
